import React, { useState } from "react";
import {
  useChangePasswordMutation,
  useCreateUserMutation,
} from "../../app/api/authApi";
import { useAppSelector } from "../../app/hooks";

const inputClass =
  "w-full bg-transparent border-b border-gray-200 py-3 text-[14px] text-itl-navy placeholder-gray-300 focus:outline-none focus:border-itl-navy transition-colors duration-200";

function errorMessage(error: unknown): string {
  const data = (error as { data?: { error?: string } })?.data;
  return data?.error || "Something went wrong. Please try again.";
}

function Card({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border border-gray-100 px-8 py-8">
      <h2 className="font-serif text-[22px] leading-tight">{title}</h2>
      <p className="mt-2 text-[13px] text-gray-500">{description}</p>
      <div className="mt-8">{children}</div>
    </section>
  );
}

export default function AdminAccount() {
  const user = useAppSelector((state) => state.auth.user);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
  });
  const [
    changePassword,
    {
      isLoading: isChanging,
      isSuccess: passwordChanged,
      isError: passwordFailed,
      error: passwordError,
      reset: resetPassword,
    },
  ] = useChangePasswordMutation();

  const [userForm, setUserForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [
    createUser,
    {
      isLoading: isCreating,
      isSuccess: userCreated,
      isError: createFailed,
      error: createError,
      reset: resetCreate,
    },
  ] = useCreateUserMutation();

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    try {
      await changePassword(passwordForm).unwrap();
      setPasswordForm({ currentPassword: "", newPassword: "" });
    } catch {
      // rendered below
    }
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createUser({ ...userForm, role: "admin" }).unwrap();
      setUserForm({ firstName: "", lastName: "", email: "", password: "" });
    } catch {
      // rendered below
    }
  }

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-itl-blue">
        Account
      </p>
      <h1 className="mt-2 font-serif text-[32px] leading-tight">
        {user?.firstName} {user?.lastName}
      </h1>
      <p className="mt-2 text-[14px] text-gray-500">
        {user?.email} · signed in as {user?.role}
      </p>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <Card
          title="Change password"
          description="Use at least 6 characters. You stay signed in on this device."
        >
          <form onSubmit={handleChangePassword} className="flex flex-col gap-7">
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                Current password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={passwordForm.currentPassword}
                onChange={(e) => {
                  resetPassword();
                  setPasswordForm((prev) => ({
                    ...prev,
                    currentPassword: e.target.value,
                  }));
                }}
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                New password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={passwordForm.newPassword}
                onChange={(e) => {
                  resetPassword();
                  setPasswordForm((prev) => ({
                    ...prev,
                    newPassword: e.target.value,
                  }));
                }}
                className={inputClass}
              />
            </div>

            {passwordChanged && (
              <p className="text-[13px] text-emerald-600">
                Password updated.
              </p>
            )}
            {passwordFailed && (
              <p className="text-[13px] text-red-600">
                {errorMessage(passwordError)}
              </p>
            )}

            <button
              type="submit"
              disabled={isChanging}
              className="self-start bg-itl-navy text-white text-[11px] font-semibold uppercase tracking-[0.16em] px-6 py-3.5 hover:bg-itl-blue transition-colors duration-200 disabled:opacity-50"
            >
              {isChanging ? "Updating…" : "Update password"}
            </button>
          </form>
        </Card>

        <Card
          title="Add an admin"
          description="Give another team member access to the briefs inbox."
        >
          <form onSubmit={handleCreateUser} className="flex flex-col gap-7">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                  First name
                </label>
                <input
                  required
                  value={userForm.firstName}
                  onChange={(e) => {
                    resetCreate();
                    setUserForm((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }));
                  }}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Last name
                </label>
                <input
                  required
                  value={userForm.lastName}
                  onChange={(e) => {
                    resetCreate();
                    setUserForm((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }));
                  }}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                Email
              </label>
              <input
                type="email"
                required
                value={userForm.email}
                onChange={(e) => {
                  resetCreate();
                  setUserForm((prev) => ({ ...prev, email: e.target.value }));
                }}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                Temporary password
              </label>
              <input
                type="text"
                required
                minLength={6}
                value={userForm.password}
                onChange={(e) => {
                  resetCreate();
                  setUserForm((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }));
                }}
                className={inputClass}
              />
            </div>

            {userCreated && (
              <p className="text-[13px] text-emerald-600">
                Admin account created.
              </p>
            )}
            {createFailed && (
              <p className="text-[13px] text-red-600">
                {errorMessage(createError)}
              </p>
            )}

            <button
              type="submit"
              disabled={isCreating}
              className="self-start bg-itl-navy text-white text-[11px] font-semibold uppercase tracking-[0.16em] px-6 py-3.5 hover:bg-itl-blue transition-colors duration-200 disabled:opacity-50"
            >
              {isCreating ? "Creating…" : "Create admin"}
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
